import axios from "axios";

const api = axios.create({
  baseURL: "/api",
  withCredentials: true,
});

let onUnauthorized = null;

export function setUnauthorizedHandler(handler) {
  onUnauthorized = handler;
}

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401 && onUnauthorized) {
      onUnauthorized(error);
    }
    return Promise.reject(error);
  }
);

export const fetchMe = () => api.get("/auth/me").then((r) => r.data);

export const login = (username, password) =>
  api.post("/auth/login", { username, password }).then((r) => r.data);

export const logout = () => api.post("/auth/logout");

export const changePassword = (currentPassword, newPassword) =>
  api.post("/auth/change-password", {
    current_password: currentPassword,
    new_password: newPassword,
  });

export const fetchDashboardUsers = () => api.get("/users").then((r) => r.data);
export const createDashboardUser = (data) => api.post("/users", data).then((r) => r.data);
export const updateDashboardUser = (userId, data) =>
  api.patch(`/users/${userId}`, data).then((r) => r.data);
export const resetDashboardUserPassword = (userId, newPassword) =>
  api.post(`/users/${userId}/reset-password`, { new_password: newPassword });

// Полный список сотрудников для страницы «Сотрудники» (/staff) — не путать с
// fetchEmployees() ниже, который отдаёт только участников опроса для фильтров.
export const fetchStaff = () => api.get("/staff").then((r) => r.data);
export const createStaffMember = (data) => api.post("/staff", data).then((r) => r.data);
export const updateStaffMember = (employeeId, data) =>
  api.patch(`/staff/${employeeId}`, data).then((r) => r.data);
export const deleteStaffMember = (employeeId) => api.delete(`/staff/${employeeId}`);

export const fetchClientProfiles = (status = "active") =>
  api.get("/client-profiles", { params: { status } }).then((r) => r.data);
export const fetchReminderCalendar = (year, month) =>
  api.get("/client-profiles/reminder-calendar", { params: { year, month } }).then((r) => r.data);
export const createClientProfile = (data) =>
  api.post("/client-profiles", data).then((r) => r.data);
export const updateClientProfile = (profileId, data) =>
  api.patch(`/client-profiles/${profileId}`, data).then((r) => r.data);
export const patchMonthCheck = (profileId, body) =>
  api.patch(`/client-profiles/${profileId}/month-check`, body).then((r) => r.data);
export const archiveClientProfile = (profileId) =>
  api.delete(`/client-profiles/${profileId}`).then((r) => r.data);
export const fetchClientAssignees = () =>
  api.get("/client-profiles/assignees").then((r) => r.data);

export const fetchKonturClients = (params = {}) =>
  api.get("/kontur/clients", { params }).then((r) => r.data);
export const createKonturPayment = (data) =>
  api.post("/kontur/payments", data).then((r) => r.data);
export const updateKonturPayment = (paymentId, data) =>
  api.patch(`/kontur/payments/${paymentId}`, data).then((r) => r.data);
export const updateKonturComment = (clientId, data) =>
  api.patch(`/kontur/clients/${clientId}`, data).then((r) => r.data);
export const deleteKonturPayment = (paymentId) =>
  api.delete(`/kontur/payments/${paymentId}`);

export const fetchClientPayments = (params = {}) =>
  api.get("/client-payments", { params }).then((r) => r.data);
export const fetchTochkaInbox = () =>
  api.get("/client-payments/tochka-inbox").then((r) => r.data);
export const createClientPayment = (data) =>
  api.post("/client-payments", data).then((r) => r.data);
export const updateClientPayment = (paymentId, data) =>
  api.patch(`/client-payments/${paymentId}`, data).then((r) => r.data);
export const updateClientFeeComment = (clientId, data) =>
  api.patch(`/client-payments/clients/${clientId}`, data).then((r) => r.data);
export const deleteClientPayment = (paymentId) =>
  api.delete(`/client-payments/${paymentId}`);

export const fetchWeeks = () => api.get("/weeks").then((r) => r.data);
export const fetchSummary = (weekStart) => api.get(`/summary/${weekStart}`).then((r) => r.data);
export const fetchResponses = (weekStart) => api.get(`/responses/${weekStart}`).then((r) => r.data);
export const fetchHeatmap = ({ grain, period, clientId } = {}) =>
  api.get("/heatmap", { params: { grain, period, client_id: clientId } }).then((r) => r.data);
export const fetchClientBlockers = ({ grain, period, clientId } = {}) =>
  api.get("/client-blockers", { params: { grain, period, client_id: clientId } }).then((r) => r.data);
export const fetchDynamics = (employeeId) =>
  api.get("/dynamics", { params: { employee_id: employeeId } }).then((r) => r.data);
export const fetchEmployees = () => api.get("/employees").then((r) => r.data);
// Алиас полного списка (/staff), не участников опроса — для селекта ЗП в inbox.
export const fetchEmployeesBrief = fetchStaff;
export const fetchClients = () => api.get("/clients").then((r) => r.data);
export const fetchVacations = () => api.get("/vacations").then((r) => r.data);
export const saveVacations = (data) => api.post("/vacations", data).then((r) => r.data);
export const fetchProductionCalendar = (year) =>
  api.get(`/production-calendar/${year}`).then((r) => r.data);
export const createUnpaidLeave = (data) =>
  api.post("/unpaid-leaves", data).then((r) => r.data);
export const updateUnpaidLeave = (id, data) =>
  api.patch(`/unpaid-leaves/${id}`, data).then((r) => r.data);
export const deleteUnpaidLeave = (id) => api.delete(`/unpaid-leaves/${id}`);

export const fetchSubstitutionEmployees = (year) =>
  api.get("/staff-substitutions/employees", { params: { year } }).then((r) => r.data);
export const fetchSubstitutionEmployee = (id, year) =>
  api.get(`/staff-substitutions/employees/${id}`, { params: year ? { year } : {} }).then((r) => r.data);
export const createSubstitution = (data) =>
  api.post("/staff-substitutions", data).then((r) => r.data);
export const bulkCreateSubstitutions = (data) =>
  api.post("/staff-substitutions/bulk", data).then((r) => r.data);
export const updateSubstitution = (id, data) =>
  api.patch(`/staff-substitutions/${id}`, data).then((r) => r.data);
export const deleteSubstitution = (id) => api.delete(`/staff-substitutions/${id}`);

export const fetchManagerDaily = (params) =>
  api.get("/manager/daily", { params }).then((r) => r.data);
export const fetchManagerClientFrequency = (params) =>
  api.get("/manager/client-frequency", { params }).then((r) => r.data);
export const fetchManagerLoad = (params) =>
  api.get("/manager/load", { params }).then((r) => r.data);
export const fetchManagerSummary = (params) =>
  api.get("/manager/summary", { params }).then((r) => r.data);
export const fetchManagerAuthors = () =>
  api.get("/manager/authors").then((r) => r.data);

export const fetchExecutiveOverview = () =>
  api.get("/executive/overview").then((r) => r.data);

export const fetchRegulations = (category) =>
  api
    .get("/regulations", { params: category ? { category } : {} })
    .then((r) => r.data);
export const fetchRegulation = (id) =>
  api.get(`/regulations/${id}`).then((r) => r.data);
export const createRegulation = (data) =>
  api.post("/regulations", data).then((r) => r.data);
export const updateRegulation = (id, data) =>
  api.patch(`/regulations/${id}`, data).then((r) => r.data);
export const deleteRegulation = (id) => api.delete(`/regulations/${id}`);
export const uploadRegulationImage = (id, file) => {
  const form = new FormData();
  form.append("file", file);
  return api.post(`/regulations/${id}/images`, form).then((r) => r.data);
};
export const deleteRegulationImage = (imageId) =>
  api.delete(`/regulations/images/${imageId}`);
export const formatRegulationMarkdown = (body) =>
  api.post("/regulations/format-markdown", { body }).then((r) => r.data);

export const fetchVpnInfo = () => api.get("/vpn/info").then((r) => r.data);

export const unlockVpn = (password) => api.post("/vpn/unlock", { password });

export const logoutVpn = () => api.post("/vpn/logout");

export const fetchVpnGate = () =>
  api.get("/admin/vpn/gate").then((r) => r.data);

export const saveVpnGate = (password) =>
  api.put("/admin/vpn/gate", { password }).then((r) => r.data);

function getSafeVpnAssetUrl(value) {
  if (typeof value !== "string" || !value.trim()) {
    throw new Error("VPN API вернул некорректную ссылку");
  }

  const url = new URL(value, window.location.origin);
  const isHttp = url.protocol === "http:" || url.protocol === "https:";
  if (!isHttp || url.origin !== window.location.origin || !url.pathname.startsWith("/api/vpn/")) {
    throw new Error("VPN API вернул небезопасную ссылку");
  }

  return `${url.pathname}${url.search}${url.hash}`;
}

export const requestVpnConfig = (data) =>
  api.post("/vpn/request", data).then((response) => ({
    ...response.data,
    config_url: getSafeVpnAssetUrl(response.data?.config_url),
    qr_url: getSafeVpnAssetUrl(response.data?.qr_url),
  }));

export const fetchVpnAdminStatus = () =>
  api.get("/admin/vpn/status").then((r) => r.data);

export const fetchVpnAdminStats = () =>
  api.get("/admin/vpn/stats").then((r) => r.data);

export const fetchVpnAdminClients = (params = {}) =>
  api.get("/admin/vpn/clients", { params }).then((r) => r.data);

export const fetchVpnAdminPeers = () =>
  api.get("/admin/vpn/peers").then((r) => r.data);

export const revokeVpnAdminClient = (configId) =>
  api.delete(`/admin/vpn/clients/${configId}`).then((r) => r.data);

export const fetchTask = (taskId) => api.get(`/tasks/${taskId}`).then((r) => r.data);
export const fetchTaskBoard = (boardId) =>
  api.get("/tasks/board", { params: boardId ? { board_id: boardId } : {} }).then((r) => r.data);
export const fetchTaskBoards = () => api.get("/tasks/boards").then((r) => r.data);
export const createBoard = (data) => api.post("/tasks/boards", data).then((r) => r.data);
export const renameBoard = (boardId, data) => api.patch(`/tasks/boards/${boardId}`, data).then((r) => r.data);
export const deleteBoard = (boardId) => api.delete(`/tasks/boards/${boardId}`);
export const createColumn = (boardId, data) => api.post(`/tasks/boards/${boardId}/columns`, data).then((r) => r.data);
export const renameColumn = (columnId, data) => api.patch(`/tasks/columns/${columnId}`, data).then((r) => r.data);
export const deleteColumn = (columnId, moveTo) =>
  api.delete(`/tasks/columns/${columnId}`, {
    params: moveTo != null ? { move_to: moveTo } : {},
  });
export const fetchTaskAssignees = () => api.get("/tasks/assignees").then((r) => r.data);
export const createTask = (data) => api.post("/tasks", data).then((r) => r.data);
export const createTaskFromBlocker = (data) => api.post("/tasks/from-blocker", data).then((r) => r.data);
export const updateTask = (taskId, data) => api.patch(`/tasks/${taskId}`, data).then((r) => r.data);
export const moveTask = (taskId, data) => api.patch(`/tasks/${taskId}/move`, data).then((r) => r.data);
export const addTaskComment = (taskId, data) => api.post(`/tasks/${taskId}/comments`, data).then((r) => r.data);
export const deleteTaskComment = (taskId, commentId) => api.delete(`/tasks/${taskId}/comments/${commentId}`);
export const deleteTask = (taskId) => api.delete(`/tasks/${taskId}`);

async function downloadFile(url, filename) {
  const response = await api.get(url, { responseType: "blob" });
  const blobUrl = window.URL.createObjectURL(response.data);
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(blobUrl);
}

export const exportCsv = (weekStart) => downloadFile(`/export/${weekStart}/csv`, `survey_${weekStart}.csv`);
export const exportXlsx = (weekStart) =>
  downloadFile(
    `/export/${weekStart}/xlsx`,
    `survey_${weekStart}.xlsx`
  );

export const exportSubstitutionsXlsx = (year, employeeId, filename) =>
  downloadFile(
    `/staff-substitutions/export.xlsx?year=${year}` + (employeeId ? `&employee_id=${employeeId}` : ""),
    filename
  );

export const exportRemindersXlsx = (filename) =>
  downloadFile("/client-profiles/reminders.xlsx", filename);

export const exportRemindersMd = (filename) =>
  downloadFile("/client-profiles/reminders.md", filename);

export const fetchFinanceOverview = (params) =>
  api.get("/finance/overview", { params }).then((r) => r.data);
export const createFinanceAccount = (data) => api.post("/finance/accounts", data).then((r) => r.data);
export const patchFinanceAccount = (id, data) => api.patch(`/finance/accounts/${id}`, data).then((r) => r.data);
export const putFinanceAccountLink = (id, data) =>
  api.put(`/finance/accounts/${id}/links`, data).then((r) => r.data);
export const createFinanceLiability = (data) => api.post("/finance/liabilities", data).then((r) => r.data);
export const patchFinanceLiability = (id, data) => api.patch(`/finance/liabilities/${id}`, data).then((r) => r.data);
export const putFinanceLiabilityPayout = (id, data) =>
  api.put(`/finance/liabilities/${id}/payout`, data).then((r) => r.data);
export const putFinancePayroll = (data) => api.put("/finance/payroll-payouts", data).then((r) => r.data);
export const fetchFinanceExpenses = () => api.get("/finance/expenses").then((r) => r.data);
export const createFinanceExpense = (data) => api.post("/finance/expenses", data).then((r) => r.data);
export const patchFinanceExpense = (id, data) => api.patch(`/finance/expenses/${id}`, data).then((r) => r.data);
export const putFinanceExpensePayout = (id, data) =>
  api.put(`/finance/expenses/${id}/payout`, data).then((r) => r.data);
export const createFinanceExtraIncome = (data) => api.post("/finance/extra-income", data).then((r) => r.data);
export const patchFinanceExtraIncome = (id, data) => api.patch(`/finance/extra-income/${id}`, data).then((r) => r.data);
export const deleteFinanceExtraIncome = (id) => api.delete(`/finance/extra-income/${id}`);
export const fetchBankAccounts = () => api.get("/finance/bank/accounts").then((r) => r.data);
export const startBankSync = () => api.post("/finance/bank/sync").then((r) => r.data);
export const fetchBankSyncStatus = () => api.get("/finance/bank/sync/status").then((r) => r.data);
export const fetchBankTransactions = (params) =>
  api.get("/finance/bank/transactions", { params }).then((r) => r.data);
export const resolveBankTransaction = (id, body) =>
  api.post(`/finance/bank/transactions/${id}/resolve`, body).then((r) => r.data);
export const resolveTochkaInbox = (id, body) =>
  api.post(`/client-payments/tochka-inbox/${id}/resolve`, body).then((r) => r.data);

export function getApiErrorMessage(error, fallbackMessage) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") {
    return detail;
  }
  if (typeof detail === "object" && detail !== null && !Array.isArray(detail)) {
    if (typeof detail.message === "string") {
      return detail.message;
    }
  }
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0];
    if (typeof first?.msg === "string") {
      return first.msg;
    }
  }
  return fallbackMessage;
}

export function getApiErrorExtras(error) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "object" && detail !== null && !Array.isArray(detail)) {
    return detail;
  }
  return null;
}
